// Frontend feature-flag composable. Holds a singleton flag map populated at
// boot from /api/v1/me (and re-populated when the selected account changes).
//
// Usage:
//
//   import { useFeatureFlag, setFeatureFlags } from '@/composables/useFeatureFlag'
//
//   const tensor = useFeatureFlag('tensor')   // reactive boolean ref
//   <Sidebar v-if="tensor" ... />
//
// `useFeatureFlag` returns a `computed` so consumers stay reactive when
// `setFeatureFlags(map)` is called later.
//
// Behavior when the map hasn't been populated yet: every key returns `false`
// (fail-closed at the UI layer). The backend gate is still authoritative —
// these UI checks are visibility-only.

import { ref, computed } from 'vue'

const flagMap = ref({})

export function setFeatureFlags(map) {
  flagMap.value = map || {}
}

// Returns a reactive `computed` boolean for the given flag key.
// Accepts the key as a string (e.g. 'tensor').
export function useFeatureFlag(key) {
  return computed(() => !!flagMap.value[key])
}

// Imperative helper for cases that need a single boolean (no template).
export function featureEnabled(key) {
  return !!flagMap.value[key]
}

// Returns the raw map. Mostly useful for debugging / vitest assertions.
export function getFeatureFlags() {
  return flagMap.value
}

// Reset to empty — used by useAuth on logout and by tests.
export function clearFeatureFlags() {
  flagMap.value = {}
}
